/**
 * Transcript parser utilities for converting raw transcript text into structured data.
 *
 * The canonical transcript types live here and are re-exported by @govinity/validation
 * for backward compatibility.
 */
/**
 * Parses a single transcript segment line.
 * Format: [HH:MM:SS] Speaker Label: text
 *
 * @param segmentLine - A single segment line from the transcript
 * @returns Parsed segment data or null if line doesn't match format
 */
function parseSegmentLine(segmentLine) {
    // Match: [HH:MM:SS] or [HH:MM:SS.mmm] Speaker Label: text
    // The optional fractional part keeps seek/highlighting precise for sub-second
    // (e.g. roll-call) splits, while remaining backward-compatible with whole-second files.
    const match = segmentLine.match(/^\[(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?\]\s+([^:]+):\s+(.+)$/s);
    if (!match)
        return null;
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);
    // Interpret the captured digits as the fractional part of a second (e.g. "420" -> 0.42).
    const fractional = match[4] ? parseFloat(`0.${match[4]}`) : 0;
    const speakerLabel = match[5].trim();
    const text = match[6].trim();
    if (!text)
        return null;
    const startTime = hours * 3600 + minutes * 60 + seconds + fractional;
    // The badge stays whole-second; precision lives in startTime.
    const timestamp = `${match[1]}:${match[2]}:${match[3]}`;
    return {
        timestamp,
        hours,
        minutes,
        seconds,
        speakerLabel,
        text,
        startTime,
    };
}
/**
 * Gets the speaker ID for a numbered speaker (e.g., "Speaker 1" -> 0).
 *
 * @param speakerLabel - The speaker label to check
 * @returns The speaker ID if it's a numbered speaker, null otherwise
 */
function getNumberedSpeakerId(speakerLabel) {
    const speakerMatch = speakerLabel.match(/^Speaker\s+(\d+)$/i);
    if (speakerMatch) {
        // "Speaker N" format - use N-1 as speakerId (0-based, preserves Deepgram IDs)
        return parseInt(speakerMatch[1], 10) - 1;
    }
    return null;
}
/**
 * Parses transcript text into structured segments and speaker information.
 * Format: [HH:MM:SS] Speaker N: text or [HH:MM:SS] Name: text
 *
 * @param transcriptText - The transcript text to parse
 * @returns Structured transcript data with segments and speakers
 */
export function parseTranscriptIntoStructuredData(transcriptText) {
    const segments = [];
    const speakerMap = new Map();
    const namedSpeakerMap = new Map();
    // Split by double newlines (segments are separated by \n\n)
    const segmentLines = transcriptText.split(/\n\n+/).filter((line) => line.trim());
    // First pass: Find the maximum numbered speaker ID to avoid conflicts
    let maxNumberedSpeakerId = -1;
    for (const segmentLine of segmentLines) {
        const parsed = parseSegmentLine(segmentLine);
        if (!parsed)
            continue;
        const numberedSpeakerId = getNumberedSpeakerId(parsed.speakerLabel);
        if (numberedSpeakerId !== null) {
            maxNumberedSpeakerId = Math.max(maxNumberedSpeakerId, numberedSpeakerId);
        }
    }
    // Second pass: Parse segments and assign IDs
    let nextNamedSpeakerId = maxNumberedSpeakerId + 1;
    for (const segmentLine of segmentLines) {
        const parsed = parseSegmentLine(segmentLine);
        if (!parsed)
            continue;
        const { timestamp, speakerLabel, text, startTime } = parsed;
        // Determine speaker ID
        let speakerId;
        const numberedSpeakerId = getNumberedSpeakerId(speakerLabel);
        if (numberedSpeakerId !== null) {
            // "Speaker N" format - use Deepgram's 0-based ID
            speakerId = numberedSpeakerId;
        }
        else {
            // Named speaker - assign a speaker ID that won't conflict with numbered speakers
            if (namedSpeakerMap.has(speakerLabel)) {
                speakerId = namedSpeakerMap.get(speakerLabel);
            }
            else {
                // Assign ID starting after the highest numbered speaker
                // This ensures named speakers never conflict with Deepgram's numbered speaker IDs
                speakerId = nextNamedSpeakerId;
                nextNamedSpeakerId += 1;
                namedSpeakerMap.set(speakerLabel, speakerId);
            }
        }
        // Track speaker information
        if (!speakerMap.has(speakerId)) {
            speakerMap.set(speakerId, { label: speakerLabel, count: 0 });
        }
        const speakerInfo = speakerMap.get(speakerId);
        speakerInfo.count += 1;
        segments.push({
            timestamp,
            speakerId,
            speakerLabel,
            text,
            startTime,
        });
    }
    // Build speakers array
    const speakers = Array.from(speakerMap.entries()).map(([speakerId, info]) => ({
        speakerId,
        label: info.label,
        isIdentified: !/^Speaker\s+\d+$/i.test(info.label),
        occurrenceCount: info.count,
    }));
    // Sort speakers by speakerId
    speakers.sort((a, b) => a.speakerId - b.speakerId);
    return {
        segments,
        speakers,
    };
}
/**
 * Formats a seconds value as a precise `HH:MM:SS.mmm` timestamp for storage.
 *
 * Producers of the canonical transcript text should use this so sub-second, word-anchored
 * starts survive round-trips; display surfaces keep using the whole-second `timestamp` field.
 *
 * @param seconds - Time in seconds (may be fractional)
 * @returns Zero-padded `HH:MM:SS.mmm` string
 */
export const formatPreciseTimestamp = (seconds) => {
    const totalMs = Math.max(0, Math.round(seconds * 1000));
    const ms = totalMs % 1000;
    const totalSeconds = Math.floor(totalMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const pad = (value, width = 2) => String(value).padStart(width, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}.${pad(ms, 3)}`;
};
/** Default minimum transcript window width in seconds. */
export const DEFAULT_MIN_TRANSCRIPT_WINDOW_SECONDS = 60;
/**
 * Formats transcript segments as `[HH:MM:SS] Speaker: text` blocks separated by blank lines.
 *
 * @param segments - Parsed transcript segments to format
 * @returns Formatted transcript excerpt text
 */
export const formatTranscriptSegments = (segments) => segments
    .map(({ speakerLabel, text, timestamp }) => `[${timestamp}] ${speakerLabel}: ${text}`)
    .join('\n\n');
/**
 * Filters transcript segments to a time window and returns a formatted excerpt.
 *
 * @param segments - Full meeting transcript segments
 * @param startSeconds - Window start in seconds (inclusive)
 * @param endSeconds - Natural window end in seconds (exclusive upper bound for segment inclusion)
 * @param minWindowSeconds - Minimum window width; expands end when the natural window is shorter
 * @returns Formatted transcript excerpt and metadata about the resolved window
 */
export const buildTranscriptWindow = (segments, startSeconds, endSeconds, minWindowSeconds = DEFAULT_MIN_TRANSCRIPT_WINDOW_SECONDS) => {
    const resolvedEndSeconds = Math.max(endSeconds, startSeconds + minWindowSeconds);
    const windowSegments = segments.filter(({ startTime }) => startTime >= startSeconds && startTime < resolvedEndSeconds);
    return {
        excerpt: formatTranscriptSegments(windowSegments),
        resolvedEndSeconds,
        segmentCount: windowSegments.length,
    };
};
const TRUNCATION_MARKER = '\n\n... [transcript excerpt truncated] ...\n\n';
/**
 * Truncates a formatted transcript excerpt to a maximum character length.
 *
 * @param excerpt - Formatted transcript text
 * @param maxChars - Maximum allowed characters
 * @param strategy - `head` preserves the opening; `head_tail` preserves opening and closing portions
 * @returns Truncated excerpt and whether truncation was applied
 */
export const truncateTranscriptExcerpt = (excerpt, maxChars, strategy) => {
    if (excerpt.length <= maxChars) {
        return { excerpt, truncated: false };
    }
    if (strategy === 'head') {
        return {
            excerpt: excerpt.slice(0, maxChars),
            truncated: true,
        };
    }
    const markerLength = TRUNCATION_MARKER.length;
    const availableChars = Math.max(0, maxChars - markerLength);
    const headChars = Math.floor(availableChars * 0.4);
    const tailChars = availableChars - headChars;
    return {
        excerpt: `${excerpt.slice(0, headChars)}${TRUNCATION_MARKER}${excerpt.slice(excerpt.length - tailChars)}`,
        truncated: true,
    };
};
